import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { fehlerText as standardFehlerText } from "../../lib/fehler";
import { MEDIEN_ACCEPT, MedienUpload, groesse, type UploadZiel, type UploadZustand } from "../../lib/medien";

/**
 * Hochladen in die Content-Ablage (0155): mehrere Dateien auf einmal (am Handy aus der Kamerarolle), je Datei Fortschritt,
 * Pause, Fortsetzen und Abbrechen. Bricht die Verbindung ab, bleibt der Upload stehen und lässt sich fortsetzen; wählt man
 * dieselbe Datei nach dem Neuladen erneut, geht es an der Stelle weiter. Tarifgrenzen kommen als Meldung mit Weg zum Tarif.
 */

type L = (de: string, en: string) => string;

/** K-587: Größe je Video aus den Tarifrechten (media_video_mb): „2 GB“ ab 2048 MB, sonst „500 MB“; unbekannt: null */
export function videoGrenzeText(videoMb: number | null | undefined): string | null {
  if (typeof videoMb !== "number" || !Number.isFinite(videoMb) || videoMb <= 0) return null;
  return videoMb >= 2048 ? `${Math.floor(videoMb / 1024)} GB` : `${videoMb} MB`;
}

export function UploadFeld({ ziel, t, onFertig, tarifLink, tarifLabel, lang = "de", label, fehlerText = standardFehlerText, onLaufend, videoMb }: {
  ziel: UploadZiel;
  t: L;
  onFertig?: (mediaId: string) => void;
  /** Weg zum Tarif bei einer Tarifgrenze (nur Agentur; Creator sehen den Hinweis an ihre Agentur) */
  tarifLink?: string | null;
  /** Beschriftung des Knopfs zum Tarif (K-374: „Upgrade auf …“); ohne Angabe „Tarif wechseln“ */
  tarifLabel?: string;
  lang?: "de" | "en";
  label?: string;
  /** Meldung in der Sprache der Seite (Agentur: fehlerText; Creator-Portal: aus Creator-Sicht) */
  fehlerText?: (e: unknown) => string;
  /** Zahl der Uploads, die gerade laufen (z. B. Einreichen bis dahin sperren) */
  onLaufend?: (n: number) => void;
  /** K-587: Größe je Video laut Tarif (media_video_mb); ohne Angabe nennt das Feld keine Größe */
  videoMb?: number | null;
}) {
  const [liste, setListe] = useState<UploadZustand[]>([]);
  const grenze = videoGrenzeText(videoMb);
  const qc = useQueryClient();
  const userId = api.session()?.userId ?? null;
  // Unterbrochene Uploads dieser Person zu diesem Ziel (z. B. Seite geschlossen): fortsetzen durch erneute Auswahl, sonst verwerfen
  const haengend = useQuery({
    queryKey: ["media_files", "unterbrochen", ziel.deal, ziel.deliverable ?? ziel.period ?? "", userId ?? ""], enabled: !!userId,
    queryFn: () => api.select<{ id: string; file_name: string; size_bytes: number }>("media_files", {
      columns: "id, file_name, size_bytes",
      filters: [["deal_id", "eq", ziel.deal], ["status", "eq", "uploading"], ["uploaded_by", "eq", userId],
        ziel.deliverable ? ["deliverable_id", "eq", ziel.deliverable] : ["period_id", "eq", ziel.period ?? ""]],
    }),
  });
  const laufend = liste.filter((z) => z.status === "laeuft" || z.status === "vorbereiten").length;
  useEffect(() => { onLaufend?.(laufend); }, [laufend, onLaufend]);
  // Warnung beim Schließen des Fensters, solange etwas hochlädt
  useEffect(() => {
    if (!laufend) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [laufend]);
  const uploads = useRef(new Map<string, MedienUpload>());
  const input = useRef<HTMLInputElement>(null);
  const fertigGemeldet = useRef(new Set<string>());

  useEffect(() => () => { for (const u of uploads.current.values()) u.pause(); }, []);

  const aendern = (z: UploadZustand) => {
    setListe((l) => l.map((x) => (x.key === z.key ? z : x)));
    if (z.status === "fertig" && z.mediaId && !fertigGemeldet.current.has(z.key)) {
      fertigGemeldet.current.add(z.key);
      onFertig?.(z.mediaId);
    }
  };
  const waehlen = (files: FileList | null) => {
    for (const f of Array.from(files ?? [])) {
      const u = new MedienUpload(api, f, ziel, aendern);
      const vorhanden = uploads.current.get(u.zustand.key);
      if (vorhanden && vorhanden.zustand.status !== "abgebrochen") {
        void vorhanden.fortsetzen();
        continue;
      }
      uploads.current.set(u.zustand.key, u);
      setListe((l) => [...l.filter((x) => x.key !== u.zustand.key), { ...u.zustand }]);
      void u.start();
    }
    if (input.current) input.current.value = "";
  };

  return (
    <div className="co-upload stack">
      <label className="co-upload-feld">
        <input ref={input} type="file" multiple accept={MEDIEN_ACCEPT} onChange={(e) => waehlen(e.target.files)} />
        <span className="btn primary">{label ?? t("Dateien hochladen", "Upload files")}</span>
        <small className="muted">{grenze
          ? t(`Video (MP4, MOV, WebM bis ${grenze}), Bild (JPG, PNG, HEIC, WebP) oder PDF. Am Handy aus der Kamerarolle.`,
            `Video (MP4, MOV, WebM up to ${grenze}), image (JPG, PNG, HEIC, WebP) or PDF. On your phone from the camera roll.`)
          : t("Video (MP4, MOV, WebM), Bild (JPG, PNG, HEIC, WebP) oder PDF. Am Handy aus der Kamerarolle.",
            "Video (MP4, MOV, WebM), image (JPG, PNG, HEIC, WebP) or PDF. On your phone from the camera roll.")}</small>
      </label>
      {(haengend.data ?? []).filter((h) => !liste.some((z) => z.mediaId === h.id)).map((h) => (
        <div key={h.id} className="notice row co-upload-haengt">
          <span>{t(`Unterbrochen: ${h.file_name} (${groesse(h.size_bytes, lang)}). Wähle die Datei erneut, um fortzusetzen.`,
            `Interrupted: ${h.file_name} (${groesse(h.size_bytes, lang)}). Choose the file again to resume.`)}</span>
          <button type="button" onClick={() => void api.medien({ action: "abort", media: h.id }).catch(() => undefined)
            .then(() => qc.invalidateQueries({ queryKey: ["media_files", "unterbrochen"] }))}>{t("Verwerfen", "Discard")}</button>
        </div>
      ))}
      {liste.length > 0 && (
        <ul className="co-upload-liste">
          {liste.map((z) => {
            const u = uploads.current.get(z.key);
            const pct = z.size ? Math.min(100, Math.round((z.loaded / z.size) * 100)) : 0;
            const grenze = z.error?.hint?.startsWith("limit:");
            // Nachtest CO-007: falscher Dateityp, zu groß oder keine Berechtigung lässt sich nicht fortsetzen, nur aus der Liste nehmen
            const endgueltig = !!grenze || ["media:type", "media:size", "media:type_mismatch"].includes(z.error?.hint ?? "") || z.error?.code === "42501";
            return (
              <li key={z.key} className={`co-upload-${z.status}`}>
                <div className="co-upload-kopf">
                  <span className="co-upload-name">{z.name}</span>
                  <span className="muted">{groesse(z.loaded, lang)} / {groesse(z.size, lang)}</span>
                </div>
                <progress max={100} value={z.status === "fertig" ? 100 : pct} aria-label={t(`Fortschritt ${z.name}`, `Progress ${z.name}`)} />
                <div className="row co-upload-aktionen">
                  <span className="co-upload-stand">{
                    z.status === "vorbereiten" ? t("Wird vorbereitet …", "Preparing …")
                    : z.status === "laeuft" ? t(`${pct} % hochgeladen`, `${pct} % uploaded`)
                    : z.status === "pausiert" ? t("Angehalten", "Paused")
                    : z.status === "fertig" ? t("Fertig", "Done")
                    : z.status === "abgebrochen" ? t("Abgebrochen", "Cancelled")
                    : z.error ? fehlerText(z.error) : t("Fehler", "Error")}</span>
                  {z.status === "laeuft" && <button type="button" onClick={() => u?.pause()}>{t("Pause", "Pause")}</button>}
                  {(z.status === "pausiert" || (z.status === "fehler" && !endgueltig)) && (
                    <button type="button" className="primary" onClick={() => void u?.fortsetzen()}>{t("Fortsetzen", "Resume")}</button>
                  )}
                  {(["vorbereiten", "laeuft", "pausiert"].includes(z.status) || (z.status === "fehler" && !endgueltig)) && (
                    <button type="button" onClick={() => void u?.abbrechen()}>{t("Abbrechen", "Cancel")}</button>
                  )}
                  {z.status === "fehler" && endgueltig && (
                    <button type="button" onClick={() => { if (z.mediaId) void u?.abbrechen(); uploads.current.delete(z.key); setListe((l) => l.filter((x) => x.key !== z.key)); }}>{t("Entfernen", "Remove")}</button>
                  )}
                  {grenze && tarifLink && <Link className="btn primary" to={tarifLink}>{tarifLabel ?? t("Tarif wechseln", "Upgrade plan")}</Link>}
                  {grenze && !tarifLink && <small className="muted">{t("Bitte gib deiner Agentur Bescheid.", "Please let your agency know.")}</small>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
